/**
 * Atributos de moda (1.0 src/lib/fashionAttributes.js, transcrito el 13/10): qué pide cada
 * categoría (talla, material) y las listas de tallas por tipo. Colores, materiales y estampados
 * viven en `packages/i18n/meta/attribute-structure.json` (ids + clave i18n).
 */
const halfRange = (from: number, to: number): string[] => {
  const out: string[] = [];
  for (let s = from; s <= to; s += 0.5) out.push(Number.isInteger(s) ? String(s) : s.toFixed(1));
  return out;
};

export const CLOTHING_SIZES: readonly string[] = [
  'XXS',
  'XS',
  'S',
  'M',
  'L',
  'XL',
  'XXL',
  '3XL',
  '32',
  '34',
  '36',
  '38',
  '40',
  '42',
  '44',
  '46',
  '48',
  '50',
];
export const WOMEN_CLOTHING_SIZES: readonly string[] = [
  'XXXS',
  'XXS',
  'XS',
  'S',
  'M',
  'L',
  'XL',
  'XXL',
  '3XL',
  '4XL',
  '5XL',
  '30',
  '32',
  '34',
  '36',
  '38',
  '40',
  '42',
  '44',
  '46',
  '48',
  '50',
  '52',
  '54',
  'One size',
];
export const MEN_CLOTHING_SIZES: readonly string[] = [
  'XXS',
  'XS',
  'S',
  'M',
  'L',
  'XL',
  'XXL',
  '3XL',
  '4XL',
  '5XL',
  '44',
  '46',
  '48',
  '50',
  '52',
  '54',
  '56',
  '58',
  '60',
  '62',
  '64',
  'One size',
];
export const KIDS_CLOTHING_SIZES: readonly string[] = [
  '50',
  '56',
  '62',
  '68',
  '74',
  '80',
  '86',
  '92',
  '98',
  '104',
  '110',
  '116',
  '122',
  '128',
  '134',
  '140',
  '146',
  '152',
  '158',
  '164',
  '170',
  '176',
  '0-1M',
  '1-3M',
  '3-6M',
  '6-9M',
  '9-12M',
  '12-18M',
  '18-24M',
  '2Y',
  '3Y',
  '4Y',
  '5Y',
  '6Y',
  '7Y',
  '8Y',
  '9Y',
  '10Y',
  '11Y',
  '12Y',
  '13Y',
  '14Y',
  '15Y',
  '16Y',
];
export const WOMEN_SHOE_SIZES: readonly string[] = halfRange(34, 43);
export const MEN_SHOE_SIZES: readonly string[] = halfRange(38, 49);
export const KIDS_SHOE_SIZES: readonly string[] = Array.from({ length: 26 }, (_, i) =>
  String(16 + i),
);

export type FashionKind =
  | 'shoes_women'
  | 'shoes_men'
  | 'shoes_kids'
  | 'clothing_women'
  | 'clothing_men'
  | 'clothing_kids'
  | 'clothing_any'
  | 'bags'
  | 'jewelry'
  | 'watches'
  | 'accessories'
  | '';

/** Tipo de moda según la ruta de categorías (raíz → hoja). */
export function fashionKind(path: readonly string[]): FashionKind {
  const has = (id: string): boolean => path.includes(id);
  if (has('shoes_women')) return 'shoes_women';
  if (has('shoes_men')) return 'shoes_men';
  if (has('shoes_kids')) return 'shoes_kids';
  if (has('fashion_women')) return 'clothing_women';
  if (has('fashion_men')) return 'clothing_men';
  if (has('fashion_kids')) return 'clothing_kids';
  if (has('bags') || has('men_bags')) return 'bags';
  if (has('jewelry') || has('men_jewelry')) return 'jewelry';
  if (has('watches') || has('men_watches')) return 'watches';
  if (has('women_accessories') || has('men_accessories')) return 'accessories';
  if (has('fashion')) return 'clothing_any';
  return '';
}

const SIZES_BY_KIND: Partial<Record<FashionKind, readonly string[]>> = {
  shoes_women: WOMEN_SHOE_SIZES,
  shoes_men: MEN_SHOE_SIZES,
  shoes_kids: KIDS_SHOE_SIZES,
  clothing_women: WOMEN_CLOTHING_SIZES,
  clothing_men: MEN_CLOTHING_SIZES,
  clothing_kids: KIDS_CLOTHING_SIZES,
  clothing_any: CLOTHING_SIZES,
};

export function sizeOptions(path: readonly string[]): readonly string[] {
  return SIZES_BY_KIND[fashionKind(path)] ?? [];
}

/** ¿Tiene sentido pedir talla? (bolsos, joyas, relojes y accesorios no). */
export function supportsSize(path: readonly string[]): boolean {
  const k = fashionKind(path);
  return k !== '' && !['bags', 'jewelry', 'watches', 'accessories'].includes(k);
}
export function supportsFashionAttributes(path: readonly string[]): boolean {
  return fashionKind(path) !== '';
}
export function supportsMaterial(path: readonly string[]): boolean {
  return (
    fashionKind(path) !== '' ||
    ['home_furniture', 'home_decor', 'home_textiles'].some((id) => path.includes(id))
  );
}

/** Tramos de peso de la 1.0 (shippingTiers.js); `settings/shipping` los sustituirá en la fase 2. */
export const WEIGHT_TIERS = [
  { tier: 1, maxKg: 2, grams: 2000 },
  { tier: 2, maxKg: 5, grams: 5000 },
  { tier: 3, maxKg: 10, grams: 10000 },
  { tier: 4, maxKg: 20, grams: 20000 },
  { tier: 5, maxKg: 40, grams: 40000 },
] as const;
export const tierToGrams = (tier: number): number =>
  WEIGHT_TIERS.find((t) => t.tier === tier)?.grams ?? 2000;
